import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Separator } from '@/Components/ui/separator';
import { Button } from '@/Components/ui/button';
import { siteConfig } from '@/config/site';
import {
    Target,
    ArrowDown,
    Clock,
    CalendarDays,
    Percent,
    CheckCircle2,
    ShieldAlert,
    CheckCircle,
    ArrowRight,
} from 'lucide-react';

const rules = [
    {
        icon: Target,
        title: 'Profit Targets',
        items: [
            'Regular & Swing: Phase 1 — 10%, Phase 2 — 8%',
            'Fast FX: Single phase — 15%',
            'Targets must be reached to pass evaluation',
        ],
    },
    {
        icon: ArrowDown,
        title: 'Drawdown Limits',
        items: [
            '5% maximum daily drawdown',
            '10% maximum overall drawdown',
            'Calculated based on balance or equity (whichever is higher)',
        ],
    },
    {
        icon: Clock,
        title: 'Time Limits',
        items: [
            'No time limit on evaluation phases',
            'Unlimited duration to reach profit targets',
            'Trade at your own pace without pressure',
        ],
    },
    {
        icon: CalendarDays,
        title: 'Minimum Trading Days',
        items: [
            '3 profitable trading days minimum per phase',
            'A trading day counts when at least one trade is opened or closed',
            'No maximum trading days requirement',
        ],
    },
    {
        icon: Percent,
        title: 'Profit Split',
        items: [
            '80% of profits go to the trader',
            'Payouts processed regularly',
            'No hidden fees on profit withdrawals',
        ],
    },
    {
        icon: CheckCircle,
        title: 'Allowed Strategies',
        items: [
            'All trading styles permitted (scalping, day trading, swing)',
            'Expert Advisors (EAs) are allowed',
            'News trading allowed on Swing plan',
            'Hedging is permitted',
        ],
    },
    {
        icon: ShieldAlert,
        title: 'Prohibited Activities',
        items: [
            'Toxic trading strategies (latency arbitrage, etc.)',
            'Account management by third parties',
            'Copy trading between evaluation accounts',
            'Exploiting platform errors or data feed issues',
        ],
    },
];

export default function Rules() {
    return (
        <AppLayout title={`Trading Rules - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <FadeIn>
                    <div className="text-center max-w-4xl mx-auto">
                        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
                            Rules & Guidelines
                        </p>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Trading Rules &{' '}
                            <span className="text-gradient">Guidelines</span>
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
                            Clear, transparent rules designed to help you
                            succeed as a funded trader.
                        </p>
                    </div>
                </FadeIn>
            </Section>

            {/* Rules Grid */}
            <Section className="bg-muted/30 border-t border-border">
                <SectionHeader
                    title="Trading"
                    highlight="Rules"
                    description="Everything you need to know about our evaluation and funded account guidelines."
                />
                <FadeIn>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {rules.map((rule) => {
                            const Icon = rule.icon;
                            return (
                                <Card
                                    key={rule.title}
                                    className="rounded-xl border border-border bg-card/80 backdrop-blur-sm p-0"
                                >
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-foreground">
                                                {rule.title}
                                            </h3>
                                        </div>
                                        <ul className="space-y-2">
                                            {rule.items.map((item) => (
                                                <li
                                                    key={item}
                                                    className="flex items-start gap-2 text-sm text-muted-foreground"
                                                >
                                                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-background border-t border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Ready to Get{' '}
                            <span className="text-gradient">Funded</span>?
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Now that you know the rules, start your journey to
                            becoming a funded trader.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/funding/overview">
                                <Button size="lg" variant="outline">
                                    View Programs
                                </Button>
                            </Link>
                            <a
                                href={siteConfig.portal.register}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button size="lg">
                                    Start Challenge
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
